import { createContext, Dispatch, memo, ReactNode, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { TabStrategyType } from "./types";

export type TabsContextProps = {
  activeTab: string,
  setActiveTab: Dispatch<SetStateAction<string>>,
  strategy: TabStrategyType,
  registerTab: (name: string) => void,
  unregisterTab: (name: string) => void,
}

const TabsContext = createContext<TabsContextProps | undefined>(undefined);

export function useTabsContext(): TabsContextProps {
  return useContext(TabsContext)!;
}

export const TabsContextProvider = memo<{
  children?: ReactNode,
  defaultActiveTabId?: string,
  strategy: TabStrategyType
}>((
  {
    children,
    defaultActiveTabId,
    strategy,
  }) => {
  const [ tabStore, setTabsStore ] = useState<string[]>([]);
  const [ activeTab, setActiveTab ] = useState("");

  const noActiveTab = !tabStore.find(x => x === activeTab);
  const tabId = tabStore[0];

  useEffect(() => {
    if (noActiveTab) {
      defaultActiveTabId ? setActiveTab(defaultActiveTabId) : setActiveTab(tabId || "");
    }
  }, [ noActiveTab, tabId, defaultActiveTabId ]);

  const registerTab = useCallback((id: string) => {
    setTabsStore(prevState => [ ...prevState, id ]);
  }, [ setTabsStore ]);

  const unregisterTab = useCallback((id: string) => {
    setTabsStore(prevState => prevState.filter(x => x !== id));
  }, [ setTabsStore ]);

  const value = useMemo(() => ({
    activeTab, setActiveTab, unregisterTab, registerTab, strategy,
  }), [ setActiveTab, activeTab, unregisterTab, registerTab, strategy ]);

  return (
    <TabsContext.Provider value={value}>
      {children}
    </TabsContext.Provider>
  );
});
